<template>
  <modal
    event-tracking-source="sign up popup"
    modal-id="signup"
    class="signup-modal"
    @close="closeModalHandler"
  >
    <div
      class="flex flex-col sm:flex-row w-full h-full py-8 overflow-y-auto sm:py-0 bg-pattern"
      :class="{ 'bg-pattern-small': isShowMobileForm }"
    >
      <SignupDescription
        :is-show-mobile-form="isShowMobileForm"
        :has-referral-code="hasReferralCode"
      />

      <div
        class="w-full h-full flex flex-col sm:block px-4 pt-3 sm:py-4 lg:py-12 sm:px-6 lg:px-12 sm:rounded-r-xl sm:w-auto right-side"
        :class="{ 'justify-between': !isShowMobileForm }"
      >
        <div
          class="text-3xl font-semibold text-center sm:text-lg sm:font-normal text-b30 hidden sm:flex justify-center"
        >
          Create a free trial account
        </div>

        <SignupButtons
          :proxy-redirect="proxyRedirect"
          :is-show-mobile-form="isShowMobileForm"
          :continue-with-email="continueWithEmail"
        />

        <div class="hidden sm:flex flex-col">
          <div class="relative z-10 mt-5 text-sm text-center or sm:mt-8">
            <span class="px-4 bg-white">or</span>
          </div>
          <SignupForm
            ref="signupForm"
            :global-error="globalError"
            :submit-form="submitForm"
            :disable-submit-btn="isSubmitting"
            :submitting="isSubmitting"
            :response-errors="responseErrors"
            @validated="validated = $event"
            @submitting="isSubmitting = $event"
          />
          <vue-recaptcha
            ref="recaptcha"
            size="invisible"
            :sitekey="recaptchaSiteKey"
            @verify="onCaptchaVerified"
            @expired="onCaptchaExpired"
          />
        </div>

        <div
          v-if="isShowMobileForm"
          class="flex sm:hidden"
        >
          <div
            class="w-full px-4 pb-4 flex-col sm:p-6 sm:pr-6 sm:pb-0 lg:pb-0 lg:p-16 lg:pr-32 rounded-l-xl sm:hidden"
          >
            <div class="flex items-center pb-8">
              <div>
                <i
                  class="fa-regular fa-angle-left text-xl"
                  @click="showMobileForm"
                />
              </div>
              <div class="font-bold text-b30 sm:mt-0 text-2xl flex justify-center w-full">
                Sign up for free
              </div>
            </div>
            <SignupForm
              ref="signupForm"
              :global-error="globalError"
              :submit-form="submitForm"
              :disable-submit-btn="isSubmitting"
              :submitting="isSubmitting"
              :response-errors="responseErrors"
              @validated="validated = $event"
              @submitting="isSubmitting = $event"
            />
            <vue-recaptcha
              ref="recaptcha"
              size="invisible"
              :sitekey="recaptchaSiteKey"
              @verify="onCaptchaVerified"
              @expired="onCaptchaExpired"
            />
          </div>
        </div>

        <div
          class="flex flex-col justify-center mb-2 sm:mb-0 pb-2 sm:pb-0"
          :class="{ 'mt-auto': isShowMobileForm }"
        >
          <div class="mt-3 text-center">
            <span class="text-sm text-b50 hidden sm:inline-block"> Already a member? </span>
            <span class="inline-block sm:hidden text-sm text-b50"> Already have an account? </span>
            <button
              type="button"
              class="text-sm text-ma"
              @click.prevent="openSignInModal('Sign In')"
            >
              Sign In
            </button>
          </div>
          <p class="text-b50 text-xs text-center mt-6 mb-5 sm:mt-8 sm:mb-0 sm:pb-0">
            By registering, you agree to the
            <a
              href="/terms-of-service/"
              class="text-b50 underline sm:text-ma sm:no-underline"
            >
              Terms of Use
            </a>
            and
            <a
              href="/privacy-policy/"
              class="text-b50 underline sm:text-ma sm:no-underline"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import SocialSignOn from '@/mixins/SocialSignOn';
import analyticsEvents from '@/utils/analyticsEvents';
import { eventTracker } from '@/utils/eventTracker';
import { getAssetPath } from '@/utils/contentDelivery';
import gtmEvents from '@/utils/gtmEvents';
import { dataLayerTrack } from '@/utils/datalayer';
import { getBasicUserData } from '@/utils/gtmEventTracker';
import getContext from '@/utilities/get-context.js';
import Modal from './Modal';

import SignupDescription from './SignUp/description.vue';
import SignupButtons from './SignUp/buttons.vue';
import SignupForm from './SignUp/form.vue';

export default {
  components: {
    Modal,
    VueRecaptcha,
    SignupDescription,
    SignupButtons,
    SignupForm,
  },
  mixins: [SocialSignOn],
  inject: ['hasPromo', 'hasHolidayPromo'],
  props: {
    socialError: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      signUpData: {
        recaptchaToken: '',
      },
      globalError: '', // Global Error independent of field
      recaptchaSiteKey: process.env.MIX_RECAPTCHA_SITE_KEY,
      isShowMobileForm: false,
      isSubmitting: false,
      responseErrors: {},
      validated: false,
    };
  },
  computed: {
    screenHeight() {
      return typeof window !== 'undefined' && window.innerHeight;
    },
    referralCode() {
      return this.$store.state.subscription.referralCode;
    },
    hasReferralCode() {
      return this.referralCode?.length > 0;
    },
  },
  watch: {
    socialError(newVal) {
      this.globalError = newVal;
    },
  },
  mounted() {
    if (typeof window !== 'undefined') {
      // Load recaptcha script
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=vueRecaptchaApiLoaded&render=explicit';
      script.async = true;
      document.body.appendChild(script);
    }
  },
  methods: {
    submitForm() {
      // Validation
      this.$refs.signupForm.validateForm();
      if (!this.$refs.signupForm.isFormValid) {
        return;
      }

      this.isSubmitting = true;
      // Execute recaptcha after validation passed
      this.$refs.recaptcha.execute();
    },

    proxyRedirect(provider) {
      eventTracker(analyticsEvents.clickOnSocialRegister, { provider });
      const rememberMe = true;

      this.redirectTo(provider, rememberMe);
    },

    saveUrl() {
      const isBrowse = window.location.pathname.startsWith('/browse');
      const categorySlugs = window.__MA__.browse_category_filters.categories.map(c => c.slug);
      const isAsset = categorySlugs.some(slug => window.location.pathname.startsWith(`/${slug}`));
      const isHome = this.$route.path === '/';

      if (!isBrowse && !isAsset && !isHome) {
        return;
      }

      sessionStorage.setItem('redirectTo', window.location.href);
    },

    sendRequest() {
      // New design does not have terms & conditions checkbox,
      // We are faking it
      const data = {
        terms: true,
        recaptchaToken: this.signUpData.recaptchaToken,
        ...this.$refs.signupForm.signUpData,
      };

      if (this.referralCode) {
        data.referral_code = this.referralCode;
      }

      window.axios
        .post('/api/sign-up/free', data)
        .then((response) => {
          this.trackSignupEvent({
            userId: response.data.user_id,
            email: data.email,
            method: 'email',
          });
          if (this.signInRedirect) {
            window.location.href = this.signInRedirect;
          } else if (response.data && response.data.redirect) {
            if (response.data.redirect.includes('thank-you')) {
              this.saveUrl();
            }
            window.location.href = response.data.redirect;
          }
        })
        .catch((error) => {
          this.$refs.recaptcha.reset();
          this.isSubmitting = false;
          // eslint-disable-next-line no-shadow
          const { data } = error.response;
          this.responseErrors = data.errors;
          let reason = 'multiple errors';
          if (typeof data.message !== 'undefined') {
            // eslint-disable-next-line no-multi-assign
            reason = this.globalError = data.message;
          }
          eventTracker(analyticsEvents.registrationFailed, {
            reason,
            registration_method: 'email',
            user_agent: navigator.userAgent,
            referred_url: document.referrer,
          });
        });
    },
    openModal(open = true) {
      this.$store.dispatch('setOpenSignUpModal', { open, clickSource: 'sign up popup' });
    },
    openSignInModal(buttonName) {
      this.openModal(false);
      this.$nextTick(() => {
        this.$store.dispatch('setOpenSignInModal', {
          open: true,
          clickSource: 'sign up popup',
          buttonName,
        });
      });
    },
    onCaptchaVerified(token) {
      this.signUpData.recaptchaToken = token;
      this.sendRequest();
    },
    onCaptchaExpired() {
      this.$refs.recaptcha.reset();
    },
    getAssetPath,
    closeModalHandler() {
      this.openModal(false);
      this.isShowMobileForm = false;
    },
    showMobileForm() {
      this.isShowMobileForm = !this.isShowMobileForm;
    },
    continueWithEmail() {
      this.isShowMobileForm = true;
      eventTracker(analyticsEvents.clickOnEmailRegister, { provider: 'email' });
    },
    trackSignupEvent({
      userId, email, method, trackOnce = false,
    }) {
      eventTracker(analyticsEvents.register, {
        user_id: userId,
        method,
        registration_method: method,
        user_agent: navigator.userAgent,
        referred_url: document.referrer,
      });

      const userBasicData = getBasicUserData({
        userId,
        email,
      });
      const trackOnceUserId = trackOnce ? (getContext().__MA__.user.id || '').toString() : null;
      dataLayerTrack({ eventName: gtmEvents.completeSignup, data: userBasicData, trackOnceUserId });
    },
  },
};
</script>

<style scoped>
.bg-pattern {
  background: url(https://assets.motionarray.com/backgrounds/mobile-signup-bg.jpg);
  background-repeat: no-repeat;
  background-size: 100% 148px;
  background-position: top;
}
.bg-pattern-small {
  background-size: 100% 88px;
}
.or {
  color: #828282;
}
.or:before {
  content: '';
  width: 100%;
  display: block;
  height: 1px;
  background-color: #efefef;
  position: absolute;
  top: 50%;
  z-index: -1;
}
>>> .modal-header path {
  fill: #828282;
}
>>> .modal-body {
  background-color: white;
  border-radius: 12px;
}
>>> .modal-container {
  margin-top: 0;
  padding-top: 6px;
}
.signup-modal >>> .modal-container {
  padding-top: 0;
  overflow-y: initial;
}
.signup-modal >>> .modal-container .close {
  color: #fff;
}
@screen sm {
  .bg-pattern {
    background: transparent;
  }
  .right-side {
    max-width: 500px;
  }
  >>> .modal-header path {
    fill: white;
  }
  >>> .modal-container {
    margin-top: 6px;
    padding-top: 0;
  }
}
@media (max-width: 639px) {
  >>> .modal-body {
    height: 100%;
  }
}
</style>
